// Shared helpers for persisting Term Journey identifiers.
// Sensitive journey identifiers live in sessionStorage.
// Only the session reference id is stored in localStorage.

const APP_NUMBER_KEY = 'termJourneyApplicationNumber';
const CASE_ID_KEY = 'termJourneyCaseId';
const SESSION_REF_KEY = 'termJourneySessionRef';

const getLocalStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch {
    // Access to localStorage may fail in some environments (e.g. SSR)
  }
  return null;
};

const getSessionStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage;
    }
  } catch {
    // Access to sessionStorage may fail in some environments (e.g. SSR)
  }
  return null;
};

const localStorageRef = getLocalStorage();
const sessionStorageRef = getSessionStorage();

export const getStoredApplicationNumber = () => {
  if (!sessionStorageRef) return null;
  return sessionStorageRef.getItem(APP_NUMBER_KEY) || null;
};

export const setStoredApplicationNumber = (applicationNumber) => {
  if (!sessionStorageRef) return;
  if (applicationNumber == null || applicationNumber === '') {
    sessionStorageRef.removeItem(APP_NUMBER_KEY);
    return;
  }
  sessionStorageRef.setItem(APP_NUMBER_KEY, applicationNumber);
};

export const clearStoredApplicationNumber = () => {
  if (!sessionStorageRef) return;
  sessionStorageRef.removeItem(APP_NUMBER_KEY);
};

export const getStoredCaseId = () => {
  if (!sessionStorageRef) return null;
  return sessionStorageRef.getItem(CASE_ID_KEY) || null;
};

export const setStoredCaseId = (caseId) => {
  if (!sessionStorageRef) return;
  if (caseId == null || caseId === '') {
    sessionStorageRef.removeItem(CASE_ID_KEY);
    return;
  }
  sessionStorageRef.setItem(CASE_ID_KEY, caseId);
};

export const clearStoredCaseId = () => {
  if (!sessionStorageRef) return;
  sessionStorageRef.removeItem(CASE_ID_KEY);
};

const createSessionRefId = () => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // ignore and use fallback
  }
  return `${Date.now()}-fallback`;
};

export const getStoredSessionRef = () => {
  if (!localStorageRef) return null;
  return localStorageRef.getItem(SESSION_REF_KEY) || null;
};

export const setStoredSessionRef = (sessionRef) => {
  if (!localStorageRef) return;
  if (sessionRef == null || sessionRef === '') {
    localStorageRef.removeItem(SESSION_REF_KEY);
    return;
  }
  localStorageRef.setItem(SESSION_REF_KEY, sessionRef);
};

export const ensureStoredSessionRef = () => {
  const existing = getStoredSessionRef();
  if (existing) return existing;
  const created = createSessionRefId();
  setStoredSessionRef(created);
  return created;
};

export const clearStoredSessionRef = () => {
  if (!localStorageRef) return;
  localStorageRef.removeItem(SESSION_REF_KEY);
};

